"use client";

import { motion } from "framer-motion";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import Link from "next/link";

export default function FAQ() {
  const faqs = [
    {
      question: "What does ZySoftec specialize in?",
      answer:
        "ZySoftec specializes in building MVPs, SaaS platforms, AI-powered products, web applications, mobile applications, and custom business software designed for long-term growth.",
    },
    {
      question: "Do you work with startups?",
      answer:
        "Yes. Many of our clients are startup founders looking to validate an idea, launch an MVP, or build a scalable product foundation before investing heavily in growth.",
    },
    {
      question: "Can you help turn my idea into an MVP?",
      answer:
        "Absolutely. We work closely with founders to define requirements, create product roadmaps, design user experiences, and build launch-ready MVPs that can scale as the business grows.",
    },
    {
      question: "Do you build AI products and AI integrations?",
      answer:
        "Yes. We develop AI assistants, workflow automation tools, AI-powered SaaS products, intelligent search systems, analytics platforms, and custom AI integrations tailored to specific business needs.",
    },
    {
      question: "How long does it take to build an MVP?",
      answer:
        "Most MVPs can be launched within a few weeks to a few months depending on complexity, features, integrations, and product requirements. We provide realistic timelines during discovery.",
    },
    {
      question: "Do you only build new products?",
      answer:
        "No. We also help businesses improve existing applications, modernize legacy systems, add new features, improve scalability, and optimize performance.",
    },
    {
      question: "Will I own the source code?",
      answer:
        "Yes. Upon project completion and final payment, you receive full ownership of the source code, assets, and deliverables unless otherwise agreed.",
    },
    {
      question: "What technologies do you use?",
      answer:
        "We primarily work with Next.js, React, React Native, Node.js, TypeScript, PostgreSQL, MongoDB, AI frameworks, cloud infrastructure, and modern scalable architectures.",
    },
    {
      question: "Do you provide support after launch?",
      answer:
        "Yes. We offer ongoing maintenance, feature development, performance improvements, bug fixes, infrastructure support, and product scaling assistance after launch.",
    },
  ];

  return (
    <section className="py-20 bg-[#F8FAFC]">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <h2
            className="text-[28px] sm:text-[34px] md:text-[40px] text-[#0F172A] mb-4"
            style={{
              fontWeight: 500,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            Frequently Asked Questions
          </h2>

          <p
            className="text-[16px] md:text-[17px] text-[#64748B] max-w-[650px] mx-auto"
            style={{
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Everything you need to know about working with ZySoftec.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: 0.1,
          }}
        >
          <Accordion.Root
            type="single"
            collapsible
            className="space-y-3"
          >
            {faqs.map((faq, index) => (
              <Accordion.Item
                key={index}
                value={`item-${index}`}
                className="overflow-hidden rounded-2xl bg-white border border-[#E5E7EB] transition-all duration-300 hover:border-[#ED7C3F]/30"
              >
                <Accordion.Header>
                  <Accordion.Trigger className="group flex w-full items-center justify-between p-5 text-left">
                    <span
                      className="pr-4 text-[15px] text-[#0F172A] transition-colors group-hover:text-[#ED7C3F]"
                      style={{
                        fontWeight: 500,
                      }}
                    >
                      {faq.question}
                    </span>

                    <ChevronDown
                      className="h-5 w-5 flex-shrink-0 text-[#64748B] transition-transform duration-300 group-data-[state=open]:rotate-180"
                    />
                  </Accordion.Trigger>
                </Accordion.Header>

                <Accordion.Content className="overflow-hidden data-[state=open]:animate-slideDown data-[state=closed]:animate-slideUp">
                  <div
                    className="px-5 pb-5 text-[14px] text-[#64748B]"
                    style={{
                      fontWeight: 400,
                      lineHeight: 1.7,
                    }}
                  >
                    {faq.answer}
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: 0.2,
          }}
          className="text-center mt-12"
        >
          <p
            className="text-[14px] text-[#64748B] mb-3"
            style={{
              fontWeight: 400,
            }}
          >
            Still have questions?
          </p>

         <Link href={'/contact'}><button
            className="text-[14px] text-[#ED7C3F] hover:underline"
            style={{
              fontWeight: 500,
            }}
          >
            Schedule a free consultation →
          </button></Link> 
        </motion.div>
      </div>
    </section>
  );
}